import { MotionConfig } from "framer-motion";
import { CartProvider } from "@/features/cart";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";
import { useHeroMotion } from "@/features/shop/components/hero/useHeroMotion";
import { CircleVideo } from "./components/CircleVideo";
import { ScrollGlobe } from "./components/ScrollGlobe";
import { useFranchiseSections } from "./useFranchiseSections";

/**
 * La landing PÚBLICA de la franquicia digital — `/franquicias`.
 *
 * Réplica de `FranchisePublicPage` del sistema actual (rama
 * `feat/v2-modelo-paquetes`): una experiencia inmersiva de desplazamiento
 * con el vídeo circular de marca flotando entre cuatro secciones.
 *
 * ── Qué hace esta pantalla, y qué no ─────────────────────────────────
 * Compone, y nada más. Las secciones son datos (`useFranchiseSections`),
 * el armazón inmersivo es `ScrollGlobe` y el contenido vive en
 * `franchise.content.ts`. Aquí no hay ni un `<div>`: solo el cromo del
 * sitio y el único estado que varias piezas comparten, la moneda.
 *
 * ── Tres diferencias obligadas con el original ───────────────────────
 * 1. El cobro SÍ está: la tarjeta del cierre genera el link de pago de
 *    Stripe, y la moneda decide el importe y la cuenta que cobra (MXN →
 *    México, USD → Estados Unidos), igual que allí. Dos diferencias que
 *    quedan: el llamado de la PORTADA sigue yendo a `/register` (el
 *    porqué está en `useFranchiseSections`), y aquí no se exige haber
 *    entrado para pagar. Todo lo que falta para que un pago quede
 *    registrado y pague comisión está en `FranchisePricingCard` y en
 *    `shared/src/contracts/checkout.ts`.
 * 2. Allí la segunda acción va a `/dashboard/mayoristas`. Aquí a
 *    `/paquetes`, que es la página pública equivalente; el porqué del
 *    cambio de etiqueta está en `useFranchiseSections`.
 * 3. Allí la moneda sale del país detectado (`useShopLocation`). Aquí del
 *    selector de la barra superior, que es lo que este sistema tiene, y
 *    el precio cambia MXN/USD con él.
 *
 * ── Por qué `ShopLayout` ─────────────────────────────────────────────
 * Es cromo del SITIO: la barra con el selector de moneda y el carrito, y
 * el pie. En producción esta landing vive dentro del mismo armazón —hasta
 * el punto de que `ScrollGlobe` trae un guard para que el vídeo no flote
 * sobre ese pie—, y sin él el selector de moneda desaparecería justo en
 * la página que enseña un precio en dos monedas.
 *
 * (Deuda anotada: `ShopLayout`, `useShopCurrency` y `useHeroMotion` son
 * del sitio, no de la tienda. Que vivan en `features/shop` obliga a
 * franquicias a importar de otra funcionalidad. Les toca subir a un sitio
 * neutral; se reutilizan tal cual antes que duplicarlos aquí.)
 *
 * ── Movimiento reducido ──────────────────────────────────────────────
 * Esta página es desplazamiento animado de punta a punta, así que la
 * preferencia se respeta en las cuatro capas: el vídeo no se reproduce ni
 * se descarga (queda su primer fotograma como imagen), el globo se queda
 * quieto en su primera posición, los saltos entre secciones no se
 * deslizan, y `MotionConfig reducedMotion="user"` apaga las animaciones
 * de entrada de la tarjeta de compra, que las trae de framer-motion y no
 * consultan la preferencia por su cuenta.
 */
export function FranchisePage() {
  const [currency, setCurrency] = useShopCurrency();
  const motionOk = useHeroMotion();
  const sections = useFranchiseSections(currency);

  return (
    <CartProvider>
      <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
        <MotionConfig reducedMotion="user">
          <ScrollGlobe
            sections={sections}
            globe={<CircleVideo size={340} motionOk={motionOk} />}
            motionOk={motionOk}
            /*
             * Fondo PLANO. Era `bg-gradient-to-br from-ds-cream via-ds-mist/20
             * to-ds-cream`: un degradado que salía de crema y volvía a crema
             * pasando por un lila al 20 %, o sea decoración pura sobre una
             * superficie. Se fue con el resto (regla de Anahi, 26-ago). No hay
             * nada que medir: no lleva texto encima, el texto vive dentro de
             * cada sección y sigue leyéndose sobre el mismo crema —`ds-ink`
             * sobre #F9F5EC, 14.9 : 1— que ya tenía en el 80 % del recorrido.
             */
            className="bg-ds-cream"
          />
        </MotionConfig>
      </ShopLayout>
    </CartProvider>
  );
}
